// use-api-polling.ts -- composable hook for polling a JSON endpoint on a
// fixed interval. Uses the existing api service for auth and base-URL
// handling, so no auth plumbing needs to be duplicated here.

import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../services/api'

interface UseApiPollingOptions {
  readonly path: string
  readonly intervalMs: number
  readonly enabled?: boolean
}

interface UseApiPollingResult<T> {
  readonly data: T | null
  readonly loading: boolean
  readonly error: string | null
  readonly refresh: () => void
}

export function useApiPolling<T>({ path, intervalMs, enabled = true }: UseApiPollingOptions): UseApiPollingResult<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetchData = useCallback(async () => {
    try {
      const result = await api.get<T>(path)
      setData(result.data ?? null)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data')
    } finally {
      setLoading(false)
    }
  }, [path])

  useEffect(() => {
    if (!enabled) return

    // Initial fetch
    void fetchData()

    // Start polling
    intervalRef.current = setInterval(() => {
      void fetchData()
    }, intervalMs)

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [fetchData, intervalMs, enabled])

  return { data, loading, error, refresh: fetchData }
}
